import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import {
  candidatesApi,
  type CandidateSearchFilters,
  type CandidateTaskStatus,
  type CandidateTaskType,
} from '@/features/recruiter/api/candidates.api'

export function useCandidateSearchQuery(filters: CandidateSearchFilters, enabled = true) {
  return useQuery({
    queryKey: ['recruiter', 'candidates', 'search', filters],
    queryFn: () => candidatesApi.search(filters),
    enabled,
  })
}

export function useCandidateSearchV2Query(filters: CandidateSearchFilters, enabled = true) {
  return useQuery({
    queryKey: ['recruiter', 'candidates', 'search-v2', filters],
    queryFn: () => candidatesApi.searchV2(filters),
    enabled,
  })
}

export function useAiCandidateSearch() {
  return useMutation({
    mutationFn: candidatesApi.aiSearch,
  })
}

export function useCandidateFilterOptions() {
  return useQuery({
    queryKey: ['recruiter', 'candidates', 'filter-options'],
    queryFn: candidatesApi.filterOptions,
    staleTime: 10 * 60 * 1000,
  })
}

export function useRecentCandidateSearches() {
  return useQuery({
    queryKey: ['recruiter', 'candidates', 'recent-searches'],
    queryFn: candidatesApi.recentSearches,
  })
}

export function useCandidateDetailQuery(studentProfileId: number | undefined) {
  return useQuery({
    queryKey: ['recruiter', 'candidates', studentProfileId],
    queryFn: () => candidatesApi.get(studentProfileId as number),
    enabled: studentProfileId != null,
  })
}

const SAVED_SEARCHES_KEY = ['recruiter', 'candidates', 'saved-searches']

export function useCandidateSavedSearches() {
  return useQuery({
    queryKey: SAVED_SEARCHES_KEY,
    queryFn: candidatesApi.savedSearches,
  })
}

export function useSaveCandidateSearch() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: candidatesApi.saveSearch,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: SAVED_SEARCHES_KEY }),
  })
}

export function useRenameCandidateSavedSearch() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, name }: { id: number; name: string }) => candidatesApi.renameSavedSearch(id, name),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: SAVED_SEARCHES_KEY }),
  })
}

export function useDeleteCandidateSavedSearch() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: number) => candidatesApi.deleteSavedSearch(id),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: SAVED_SEARCHES_KEY }),
  })
}

export function useCandidateTaskAssignees() {
  return useQuery({
    queryKey: ['recruiter', 'candidates', 'task-assignees'],
    queryFn: candidatesApi.taskAssignees,
    staleTime: 10 * 60 * 1000,
  })
}

const MY_TASKS_KEY = ['recruiter', 'tasks']

export function useMyCandidateTasks(params?: { assigned_to_me?: boolean; status?: CandidateTaskStatus }) {
  return useQuery({
    queryKey: [...MY_TASKS_KEY, params],
    queryFn: () => candidatesApi.myTasks(params),
  })
}

export function useCreateCandidateTask() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      studentProfileId,
      ...payload
    }: {
      studentProfileId: number
      type: CandidateTaskType
      note?: string
      due_at?: string
      assigned_to_user_id?: number
    }) => candidatesApi.createTask(studentProfileId, payload),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: MY_TASKS_KEY }),
  })
}

export function useUpdateCandidateTask() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      id,
      ...payload
    }: {
      id: number
      note?: string
      due_at?: string
      status?: CandidateTaskStatus
      assigned_to_user_id?: number
    }) => candidatesApi.updateTask(id, payload),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: MY_TASKS_KEY }),
  })
}

export function useDeleteCandidateTask() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: number) => candidatesApi.deleteTask(id),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: MY_TASKS_KEY }),
  })
}
